import { Add, BrushOutlined, CreateOutlined, DashboardCustomizeOutlined } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Fade, Grid, Paper, Tooltip, Typography } from "@mui/material";
import { useNavigate } from 'react-router-dom';

const Homepage = () => {
    const navigate = useNavigate();
    const toCanvas = () => {
        navigate("/canvas");
    };

    const features = [
        {
            icon: <BrushOutlined sx={{ fontSize: 48, color: '#00F3FF' }} />,
            title: 'Professional Tools',
            description: 'Advanced drawing and editing tools for creating stunning graphics'
        },
        {
            icon: <DashboardCustomizeOutlined sx={{ fontSize: 48, color: '#2a5298' }} />,
            title: 'Flexible Canvas',
            description: 'Multi-page support with customizable canvas sizes and orientations'
        },
        {
            icon: <CreateOutlined sx={{ fontSize: 48, color: '#1e3c72' }} />,
            title: 'Rich Features',
            description: 'Shapes, text, images, charts, and powerful manipulation tools'
        },
    ];

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #36454F 100%)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 20% 50%, rgba(0,243,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(42,82,152,0.1) 0%, transparent 50%)',
                animation: 'float 6s ease-in-out infinite',
            },
            '@keyframes float': {
                '0%, 100%': { transform: 'translate(0, 0)' },
                '50%': { transform: 'translate(-20px, -20px)' },
            },
        }}>
            {/* Hero Section */}
            <Box sx={{
                textAlign: 'center',
                pt: 8,
                pb: 6,
                px: 3,
                position: 'relative',
                zIndex: 1
            }}>
                <Fade in timeout={800}>
                    <Typography
                        variant="h1"
                        component="h1"
                        sx={{
                            color: 'white',
                            fontWeight: 800,
                            fontSize: { xs: '2.5rem', md: '4rem' },
                            letterSpacing: 3,
                            textShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
                            mb: 2
                        }}
                    >
                        The BioGraphix
                    </Typography>
                </Fade>
                <Fade in timeout={1000}>
                    <Typography
                        variant="h5"
                        sx={{
                            color: 'rgba(255, 255, 255, 0.95)',
                            fontWeight: 300,
                            mb: 5,
                            maxWidth: 600,
                            mx: 'auto'
                        }}
                    >
                        Create stunning biological diagrams and graphics with our professional canvas editor
                    </Typography>
                </Fade>

                {/* Create Project Card */}
                <Fade in timeout={1200}>
                    <Paper
                        elevation={16}
                        sx={{
                            maxWidth: 500,
                            mx: 'auto',
                            p: 5,
                            borderRadius: 4,
                            background: 'rgba(255, 255, 255, 0.98)',
                            backdropFilter: 'blur(10px)',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 5,
                                background: 'linear-gradient(90deg, #00F3FF 0%, #1e3c72 50%, #00F3FF 100%)',
                                backgroundSize: '200% 100%',
                                animation: 'shimmer 3s linear infinite',
                            },
                            '@keyframes shimmer': {
                                '0%': { backgroundPosition: '200% 0' },
                                '100%': { backgroundPosition: '-200% 0' },
                            },
                        }}
                    >
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                            <Add sx={{
                                fontSize: 64,
                                color: 'primary.main',
                                mb: 2,
                                p: 2,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(30, 60, 114, 0.1)'
                            }} />
                            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: '#1e3c72' }}>
                                Start Creating
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Begin a new project and bring your ideas to life
                            </Typography>
                        </Box>

                        <Button
                            onClick={toCanvas}
                            variant="contained"
                            size="large"
                            fullWidth
                            aria-label="Create new project"
                            sx={{
                                py: 2,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                boxShadow: '0 6px 20px rgba(30, 60, 114, 0.4)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #2a5298 0%, #00F3FF 100%)',
                                    transform: 'translateY(-3px)',
                                    boxShadow: '0 8px 28px rgba(30, 60, 114, 0.6)',
                                }
                            }}
                        >
                            <Add sx={{ mr: 1 }} />
                            Create New Project
                        </Button>
                    </Paper>
                </Fade>
            </Box>

            {/* Features Section */}
            <Box sx={{
                flex: 1,
                px: 3,
                pb: 6,
                position: 'relative',
                zIndex: 1
            }}>
                <Grid container spacing={3} sx={{ maxWidth: 1200, mx: 'auto' }}>
                    {features.map((feature, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Fade in timeout={1400 + index * 200}>
                                <Card
                                    elevation={8}
                                    sx={{
                                        height: '100%',
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: 3,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.3)',
                                        }
                                    }}
                                >
                                    <CardContent sx={{ textAlign: 'center', p: 4 }}>
                                        <Box sx={{ mb: 2 }}>
                                            {feature.icon}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: '#1e3c72' }}>
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {feature.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Fade>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
};

export default Homepage;